import { useMemo } from 'react';
import { useGqlQuery } from '@API/useAvQuery';
import { AccountEntities, Projection } from '@fabric-ui/components';
import { QueryKey } from '@fabric-ui/utils';
import { gql } from 'graphql-request';
import { ProjectionID } from '../CustomDashboards/types';

const SETTING_FOR_FIELD_BY_NAME_AND_TYPE = gql`
  query getSettingSyncStatusByFieldNameAndSettingType($fieldName: String!, $requestedSettingType: String!) {
    getSettingSyncStatusByFieldNameAndSettingType(fieldName: $fieldName, requestedSettingType: $requestedSettingType) {
      configuredThrough
      synced
      projectionToAffectedFields {
        projectionId
        fields
      }
    }
  }
`;

const SETTING_INFO_BY_TYPE = gql`
  query getSettingSyncStatusBySettingsType($requestedSettingType: String!) {
    getSettingSyncStatusBySettingsType(requestedSettingType: $requestedSettingType) {
      synced
      configuredThrough
      projectionToAffectedFields {
        projectionId
        fields
      }
    }
  }
`;

export const useIsFieldSyncedToSettingByNameAndType = (fieldName: string, requestedSettingType: string) =>
  useGqlQuery<ISyncStatus>({
    queryKey: [QueryKey.FieldSyncToSettingByNameAndType, fieldName, requestedSettingType],
    query: SETTING_FOR_FIELD_BY_NAME_AND_TYPE,
    variables: { fieldName, requestedSettingType },
    enabled: !!fieldName && !!requestedSettingType,
    gcTime: 0,
  });

export const useIsSettingSyncStatusBySettingsType = (requestedSettingType: string) =>
  useGqlQuery<ISyncStatus>({
    queryKey: [requestedSettingType, QueryKey.FieldSyncToSettingType],
    query: SETTING_INFO_BY_TYPE,
    variables: { requestedSettingType },
    enabled: !!requestedSettingType,
    gcTime: 0,
  });

const GET_LIST = gql`
  query aggProjections {
    aggProjections {
      meta {
        id {
          name
          builtIn
        }
      }
      settings
    }
  }
`;

export const useProjectionSettings = () => {
  const result = useGqlQuery<Projection[]>({ queryKey: [QueryKey.AggProjections], query: GET_LIST });
  const data: Record<Projection['meta']['id']['name'], Projection> | undefined = useMemo(
    () => result.data?.reduce((acc, curr) => ({ ...acc, [curr.meta.id.name]: curr }), {}),
    [result.data]
  );
  return { ...result, data };
};

export const useSeveritySettings = () => {
  const { data } = useProjectionSettings();
  return data?.uber_findings.settings.severities?.filter(s => s.label !== 'NONE')?.toReversed() || [];
};

export enum SettingType {
  GroupingRules = 'GROUPING_RULES',
  GroupingRulesAem = 'GROUPING_RULES_AEM',
  SeveritySLA = 'SEVERITY_AND_SLA',
  Score = 'SCORING',
  GroupingRulesAlerts = 'GROUPING_RULES_ALERTS',
  SeveritySLAAlerts = 'SEVERITY_AND_SLA_ALERTS',
  ScoreAlerts = 'SCORING_ALERTS',
  UnificationRules = 'UNIFICATION_RULES',
  Automations = 'AUTOMATIONS',
  DATA_MODEL = 'DATA_MODEL',
  STATUS_AUTOMATION = 'STATUS_AUTOMATION',
  LOCKED_AUTOMATION = 'LOCKED_AUTOMATION',
  STATUS_AUTOMATION_AEM = 'STATUS_AUTOMATION_AEM',
  LOCKED_AUTOMATION_AEM = 'LOCKED_AUTOMATION_AEM',
  ASSET_AGING = 'ASSET_AGING',
  SEVERITY_AUTOMATION = 'SEVERITY_AUTOMATION',
  SLA_AUTOMATION = 'SLA_AUTOMATION',
}

export interface IProjectionToAffectedFields {
  projectionId: ProjectionID;
  fields: string[];
}

export interface ISyncStatus {
  configuredThrough: SettingType;
  synced: boolean;
  projectionToAffectedFields: IProjectionToAffectedFields[];
}

export const SettingsName = {
  [SettingType.Score]: 'Score Settings',
  [SettingType.SeveritySLA]: 'Severity & SLA',
  [SettingType.GroupingRules]: 'Grouping Rules',
  [SettingType.UnificationRules]: 'Data Unification',
  [SettingType.DATA_MODEL]: 'Data Model',
  [SettingType.ScoreAlerts]: 'Score Settings',
  [SettingType.SeveritySLAAlerts]: 'Severity & SLA',
  [SettingType.GroupingRulesAlerts]: 'Grouping Rules',
  // Automations name is temporary and will be changed after BE refactor. Needed support for different projections
  [SettingType.Automations]: 'UVM Ticket Lifecycle',
  [SettingType.STATUS_AUTOMATION]: 'UVM Ticket Lifecycle',
  [SettingType.LOCKED_AUTOMATION]: 'UVM Ticket Lifecycle',
  [SettingType.STATUS_AUTOMATION_AEM]: 'AEM Ticket Lifecycle',
  [SettingType.LOCKED_AUTOMATION_AEM]: 'AEM Ticket Lifecycle',
  [SettingType.ASSET_AGING]: 'Asset Aging',
  [SettingType.SEVERITY_AUTOMATION]: 'Severity Settings',
  [SettingType.SLA_AUTOMATION]: 'SLA Settings',
  [SettingType.GroupingRulesAem]: 'Grouping Rules AEM',
};

export const getRuleFilterOptions = (aggProjs: AccountEntities, projectionName: string) =>
  aggProjs[aggProjs[projectionName].aggregates.name].fieldList.EVAL.filter(
    f => !f.visibilityConfig.config.hidden && !f.id.endsWith('grouping_key')
  );

export enum SaveWorkflowEnum {
  SAVE = 'save',
  SAVE_AND_RUN = 'saveAndRun',
}
